import { useState } from 'react';
import { Box, Button, Stack, Tab, Tabs } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AiFeatureLabel from 'src/components/ai-feature-label/AiFeatureLabel';
import Editor from 'src/components/editor';
import ProfessionalAssistantChat from 'src/sections/@dashboard/profile/components/ProfessionalAssistantChat';
import { AssistantAction } from 'src/sections/@dashboard/visit/utils/assistantActions';
import { textNotePreview } from 'src/sections/@dashboard/profile/utils/textNotePreview';
import { translateKey } from 'src/locales/translate';

export type PatientNoteModalLayout = 'editor-only' | 'side-by-side' | 'tabs';

export type PatientNoteModalBodyProps = {
  content: string;
  onContentChange: (value: string) => void;
  // editor-only: org admins (no assistant). side-by-side: professional on a wide
  // viewport (editor + chat columns). tabs: professional on a smaller viewport
  // where the side panel can't fit — Notatka / Asystent Vetsi tabs instead.
  layout: PatientNoteModalLayout;
  // Whether the modal is in fixed-height (paper) mode — drives the tabbed chat's
  // fillHeight (it can only fill a definite-height panel).
  isPaperScroll: boolean;
  profileId: string;
  /** Guest visits have no profile — the assistant is scoped by visit instead. */
  visitId?: string;
  patientName: string;
  onAssistantAction: (action: AssistantAction) => void;
  onClose: () => void;
  onSubmit: (content: string) => Promise<void>;
  isSaving: boolean;
  /** Controls whether the voice-dictation mic is shown in the editor.
   *  Must be false on surfaces where OrganizationProvider is not mounted,
   *  because the mic component reads org context internally. */
  enableDictation: boolean;
  /** View-only mode: the editor is locked (toolbar hidden) and the footer shows
   *  a single Close button instead of the Cancel/Save pair. Used when a note is
   *  past its edit window (e.g. a visit completed more than 30 days ago). */
  readOnly?: boolean;
};

export default function PatientNoteModalBody({
  content,
  onContentChange,
  layout,
  isPaperScroll,
  profileId,
  visitId,
  patientName,
  onAssistantAction,
  onClose,
  onSubmit,
  isSaving,
  enableDictation,
  readOnly = false,
}: PatientNoteModalBodyProps) {
  const [tab, setTab] = useState(0);

  // Left column / Notatka tab: the editor fills the available height above a
  // pinned action footer that lives in this column (not a shared full-width bar)
  // — mirroring how the OrganizationVisitModal keeps its save/cancel actions
  // under the content while the assistant fills the height of its column.
  const editorColumn = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 3, pt: 3, pb: 2 }}>
        <Editor
          id="patient-note-editor"
          simple
          value={content}
          onChange={onContentChange}
          disabled={readOnly}
          enableDictation={enableDictation}
          placeholder={translateKey('hardcoded.stworz_notatke')}
          // Fill the column: flex the react-quill chain
          // (StyledEditor → div.quill → .ql-container → .ql-editor) and lift the
          // shared editor's 640px .ql-editor cap so the field grows to the
          // available height and scrolls internally instead of leaving a gap.
          sx={{
            height: 1,
            display: 'flex',
            flexDirection: 'column',
            '& .quill': {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            },
            '& .ql-container': { flex: 1, minHeight: 0 },
            '& .ql-editor': { maxHeight: 'none' },
            // Read-only: hide the (inert) formatting toolbar so the note reads
            // as a clean document rather than a disabled editor.
            ...(readOnly && { '& .ql-toolbar': { display: 'none' } }),
          }}
        />
      </Box>
      <Box sx={{ flexShrink: 0, px: 3, py: 2 }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {readOnly ? (
            <Button
              size="medium"
              variant="contained"
              color="primary"
              onClick={onClose}
            >
              {translateKey('common.close')}
            </Button>
          ) : (
            <>
              <Button
                size="medium"
                variant="outlined"
                color="inherit"
                onClick={onClose}
                disabled={isSaving}
              >
                {translateKey('common.cancel')}
              </Button>
              <LoadingButton
                size="medium"
                variant="contained"
                color="primary"
                data-testid="note-save"
                loading={isSaving}
                disabled={isSaving || !textNotePreview(content)}
                onClick={() => {
                  void onSubmit(content);
                }}
              >
                {translateKey('common.save')}
              </LoadingButton>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );

  const renderChat = (fillHeight: boolean) => (
    <ProfessionalAssistantChat
      fillHeight={fillHeight}
      naturalOrder
      edgeScrollbar
      profileId={profileId}
      visitId={visitId}
      profileName={patientName}
      surface="patient_note"
      textNoteDraft={content}
      onAssistantAction={onAssistantAction}
    />
  );

  if (layout === 'editor-only') return editorColumn;

  if (layout === 'side-by-side') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          minHeight: 0,
          width: '100%',
        }}
      >
        {editorColumn}
        <Box
          sx={{
            width: 600,
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderLeft: '1px solid',
            borderColor: 'divider',
            p: 3,
          }}
        >
          {renderChat(true)}
        </Box>
      </Box>
    );
  }

  // tabs: keep both panels mounted (display toggle) so the editor draft and the
  // chat's cached conversation survive switching tabs.
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{
          flexShrink: 0,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tab label={translateKey('hardcoded.notatka')} />
        <Tab
          label={
            <AiFeatureLabel label={translateKey('visit.vetsiAssistantTab')} />
          }
        />
      </Tabs>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: tab === 0 ? 'flex' : 'none',
          flexDirection: 'column',
        }}
      >
        {editorColumn}
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: tab === 1 ? 'flex' : 'none',
          flexDirection: 'column',
          p: 3,
        }}
      >
        {renderChat(isPaperScroll)}
      </Box>
    </Box>
  );
}
